'use client';

/**
 * ⚠️ DEPRECATED — legacy pre-cards vendor explorer.
 *
 * Superseded by the cards-everywhere `VendorListCard`
 * (components/explorer/cards/vendors/list-card.tsx), which mode4 mounts via
 * `explorer_cards`. This component is no longer reached in the live matrix
 * (mode4 declares both `explorer="vendors"` and `explorer_cards=[...]`; the
 * card stack wins — see explorer.tsx). Kept only until the legacy dispatch is
 * removed.
 *
 * DO NOT ADD FEATURES HERE. New explorer work (e.g. the "My Favourites"
 * grouping) belongs in the cards-everywhere `VendorListCard`. Anything added
 * here is dead code that silently diverges from what users actually see.
 */

import { useState } from 'react';
import { ChevronRight, Factory, Grid2X2, Box, Loader2, Plus, Home, Lightbulb } from 'lucide-react';
import { useVendors, useVendorExplore } from '@/lib/api/hooks';
import { useTabStore } from '@/lib/stores/tab-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import type { VendorSummary } from '@/lib/api/types';
import { cn } from '@/lib/utils/cn';
import { testIds } from '@/lib/test-utils/selectors';

export function VendorsExplorer() {
  const vendorsStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'vendor_list'));
  const { data, isLoading, error } = useVendors(vendorsStrategy!, { limit: 50 });
  const openTab = useTabStore((state) => state.openTab);
  const clearSelection = useGraphSelectionStore((state) => state.clearSelection);
  const setActiveTab = useTabStore((state) => state.setActiveTab);
  const activeTabId = useTabStore((state) => state.activeTabId);
  const canAddVendor = useCapabilityStore((s) => s.strategyName('vendor_portal', 'create') !== null);

  const handleAddVendor = () => {
    openTab({
      id: 'add-vendor-form',
      type: 'add_vendor',
      title: 'Add Vendor',
      entityId: 'add-vendor',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-sm text-destructive">
        Failed to load vendors. Check connection.
      </div>
    );
  }

  const handleGoHome = () => {
    setActiveTab(null);
    clearSelection();
  };

  return (
    <div data-testid={testIds.explorer.vendorList} className="py-1">
      {/* Action bar */}
      <div className="flex items-center gap-1 px-2 mb-1">
        {activeTabId && (
          <button
            onClick={handleGoHome}
            className="flex items-center gap-1.5 px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-accent/50 rounded transition-colors"
          >
            <Home className="w-3.5 h-3.5" />
            Home
          </button>
        )}
        {canAddVendor && (
          <button
            onClick={handleAddVendor}
            className="flex items-center gap-1.5 px-2 py-1 text-xs text-primary hover:bg-accent/50 rounded transition-colors ml-auto"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Vendor
          </button>
        )}
      </div>

      {!data?.vendors.length ? (
        <div className="px-4 py-3 text-sm text-muted-foreground">
          No vendors found.
        </div>
      ) : (
        data.vendors.map((vendor) => (
          <VendorTreeItem key={vendor.semantic_id} vendor={vendor} />
        ))
      )}
    </div>
  );
}

function VendorTreeItem({ vendor }: { vendor: VendorSummary }) {
  const [expanded, setExpanded] = useState(false);
  const openTab = useTabStore((state) => state.openTab);
  const updateTab = useTabStore((state) => state.updateTab);
  const selectNode = useGraphSelectionStore((state) => state.selectNode);
  const hasChildren = vendor.collection_count > 0 || vendor.product_count > 0;

  // Only fetch when expanded
  const vendorExploreStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'vendor_explore'));
  const { data, isLoading } = useVendorExplore(
    vendorExploreStrategy!,
    expanded ? { vendor_id: vendor.semantic_id } : null
  );

  const tabId = `vendor-${vendor.semantic_id}`;

  const handleOpenInCanvas = (collectionId?: string) => {
    // First open/activate the tab (note: openTab calls clearSelection internally)
    openTab({
      id: tabId,
      type: 'vendor',
      title: vendor.name,
      entityId: vendor.semantic_id,
      selectedCollectionId: collectionId || null,
    });
    // Always update selectedCollectionId (reset to null if clicking vendor, set if clicking collection)
    updateTab(tabId, { selectedCollectionId: collectionId || null });
    // Set graph selection so copilot picks up the vendor type for matrix lookup
    selectNode({ id: vendor.semantic_id, label: vendor.name, type: 'vendor' });
  };

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasChildren) {
      setExpanded(!expanded);
    }
  };

  return (
    <div>
      {/* Vendor Row */}
      <div
        data-testid={testIds.explorer.vendorItem(vendor.semantic_id)}
        className={cn(
          'w-full flex items-center gap-1 px-2 py-1 text-sm',
          'hover:bg-accent/50 transition-colors cursor-pointer',
          !hasChildren && 'pl-6'
        )}
        onClick={() => handleOpenInCanvas()}
      >
        {hasChildren && (
          <button
            onClick={handleToggleExpand}
            className="p-0.5 hover:bg-muted rounded"
          >
            <ChevronRight
              className={cn(
                'w-4 h-4 shrink-0 transition-transform',
                expanded && 'rotate-90'
              )}
            />
          </button>
        )}
        {vendor.source === 'buyer' ? (
          <Lightbulb className="w-4 h-4 shrink-0 text-orange-400" />
        ) : (
          <Factory className="w-4 h-4 shrink-0 text-purple-400" />
        )}
        <span className="truncate flex-1 text-left">{vendor.name}</span>
        {vendor.product_count > 0 && (
          <span className="text-xs text-muted-foreground">
            {vendor.source === 'buyer'
              ? `${vendor.product_count} wish${vendor.product_count !== 1 ? 'es' : ''}`
              : `${vendor.collection_count} col, {vendor.product_count} prod`}
          </span>
        )}
      </div>

      {/* Expanded Children */}
      {expanded && (
        <div className="ml-4 border-l border-border/50">
          {isLoading ? (
            <div className="flex items-center gap-2 px-4 py-1 text-sm text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading...
            </div>
          ) : (
            <>
              {/* Collections */}
              {data?.vendor?.collections.map((collection) => (
                <CollectionTreeItem
                  key={collection.semantic_id}
                  collection={collection}
                  onOpenCollection={() => handleOpenInCanvas(collection.semantic_id)}
                />
              ))}

              {/* Uncategorized Products */}
              {data?.vendor?.uncategorized_products.map((product) => (
                <button
                  key={product.semantic_id}
                  className="w-full flex items-center gap-1 px-2 py-1 text-sm text-left hover:bg-accent/50 pl-4"
                >
                  <Box className="w-4 h-4 shrink-0 text-orange-400" />
                  <span className="truncate flex-1">{product.name}</span>
                  {product.list_price && (
                    <span className="text-xs text-muted-foreground">
                      ${product.list_price.toFixed(0)}
                    </span>
                  )}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CollectionTreeItem({
  collection,
  onOpenCollection,
}: {
  collection: { semantic_id: string; name: string; product_count: number; products: any[] };
  onOpenCollection: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  return (
    <div>
      <div
        className="w-full flex items-center gap-1 px-2 py-1 text-sm text-left hover:bg-accent/50 pl-4 cursor-pointer"
        onClick={onOpenCollection}
      >
        <button
          onClick={handleToggleExpand}
          className="p-0.5 hover:bg-muted rounded"
        >
          <ChevronRight
            className={cn(
              'w-4 h-4 shrink-0 transition-transform',
              expanded && 'rotate-90'
            )}
          />
        </button>
        <Grid2X2 className="w-4 h-4 shrink-0 text-cyan-400" />
        <span className="truncate flex-1">{collection.name}</span>
        <span className="text-xs text-muted-foreground">
          {collection.product_count}
        </span>
      </div>

      {expanded && (
        <div className="ml-4 border-l border-border/50">
          {collection.products.map((product) => (
            <button
              key={product.semantic_id}
              className="w-full flex items-center gap-1 px-2 py-1 text-sm text-left hover:bg-accent/50 pl-4"
            >
              <Box className="w-4 h-4 shrink-0 text-orange-400" />
              <span className="truncate flex-1">{product.name}</span>
              {product.list_price && (
                <span className="text-xs text-muted-foreground">
                  ${product.list_price.toFixed(0)}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
